import { useCallback, useMemo, useState } from 'react';

import { FeatureWrapper } from '@base/FeatureWrapper';
import { Entity, EntityRelationType, EntityType, FolderItemType } from '@common/api';
import { useI18nContext } from '@i18n/i18n-react';

import { Sidebar, SidebarSection, SidebarScroller } from '@components/layout/Sidebar';
import { BasicTitle } from '@components/typography/Typography';
import { SearchInput } from '@components/form';
import { useAllEntities } from '@data/entity/entity';
import { useRelationPaths, getEntityTreeRows } from '@data/entity/relation';
import { ENTITY_FILTERS, useEntitySearch } from '@data/entity/search';
import { PseudoEntityKey } from '@data/entity/types';
import { getShortKeyForEntity, toRealEntityKey, makeLiteralEntity } from '@data/entity/util';
import { useExpandClusters } from '@hooks/useExpandClusters';
import { makeLocalStorageHook } from '@hooks/useLocalStorage';
import { useEntityContext } from '@providers/EntityProvider';
import { useRelationContext } from '@providers/RelationProvider';

import { TagsTree } from './TagsTree';

const TREE_RELATION_TYPES = [EntityRelationType.Folder, EntityRelationType.Parent];
const TABLE_SELECTOR = { type: EntityType.DimensionTable };

const TAGS_SIDEBAR_STORAGE_KEY = 'tags-sidebar';
const NO_PATH: string[] = [];
const NO_RESULTS: Entity[] = [];

export const TagsSidebar = () => {
    return (
        <FeatureWrapper featureKey="canSeeTagsSidebar">
            <TagsSidebarInner />
        </FeatureWrapper>
    );
};

export const TagsSidebarInner = () => {
    const { LL } = useI18nContext();
    const { getEntity } = useEntityContext();
    const { getByRelation, isRelationLoading } = useRelationContext();

    // Search for entities (if query)
    const [query, setQuery] = useState('');
    const search = useEntitySearch(query, { filter: ENTITY_FILTERS.tag });
    // Search returns result when query is '' which will effect the expanded state
    const searchResults = query === '' ? NO_RESULTS : search;

    // Get all parents of search results
    const searchParentPaths = useRelationPaths(searchResults, TREE_RELATION_TYPES);
    const searchParentSet = useMemo(() => {
        const searchKeys = searchParentPaths.flatMap((parentPath) => parentPath.flatMap(getShortKeyForEntity));
        return new Set<string>(searchKeys);
    }, [searchParentPaths]);

    // Expand state for tree
    const expandClusters = useExpandClusters(
        NO_PATH,
        NO_PATH,
        {},
        makeLocalStorageHook(`${TAGS_SIDEBAR_STORAGE_KEY}-expanded`)
    );
    const canExpand = useCallback(
        (e: PseudoEntityKey) => e.type === EntityType.Folder || e.type === EntityType.DimensionTable,
        []
    );

    // Force-expand search result parents
    const isExpanded = useCallback(
        (path: string[]) => {
            const key = path.at(-1)!;
            if (searchParentSet.has(key)) return true;
            return expandClusters.isExpanded(path);
        },
        [searchParentSet, expandClusters]
    );

    // Build tree
    const tableVersion = useAllEntities(TABLE_SELECTOR);
    const treeRows = useMemo(() => {
        // Manually manage tree traversal
        const getRelation = (e: PseudoEntityKey | null) => {
            const entityKey = e ? toRealEntityKey(e) : null;

            const isSearchResult = query.length
                ? (e: PseudoEntityKey) => searchParentSet.has(getShortKeyForEntity(e))
                : () => true;

            // Get all children (parent and folder)
            const related = TREE_RELATION_TYPES.flatMap((rel) => getByRelation(entityKey, rel));

            // Only show dimension and variables root folders
            if (e == null) {
                const rootFolders = related.map(getEntity).filter((e) => !!e) as Entity[];
                const tagFolders = rootFolders.filter(
                    ({ folder }) =>
                        folder &&
                        (folder.allowedTypes.includes(FolderItemType.DimensionTable) ||
                            folder.allowedTypes.includes(FolderItemType.Variable))
                );
                return tagFolders.filter(isSearchResult);
            }

            // Inject table properties into tree as pseudo-entities
            /*
                    // TODO: implement column pivot zones
                    // and add handler to DragAndDropProvider for dropping properties
                    const properties =
                        e?.type === EntityType.DimensionTable ? getTableProperties(getEntity(e as EntityKey)) : [];
                    */

            // Filter to search query (if any)
            const children: PseudoEntityKey[] = [/*...properties, */ ...related].filter(isSearchResult);

            return children;
        };

        const isLoading = (e: PseudoEntityKey | null) => {
            const entityKey = toRealEntityKey(e);
            return !!(entityKey && TREE_RELATION_TYPES.some((rel) => isRelationLoading(entityKey, rel)));
        };

        const getPlaceholder = (loading: boolean, id: number) =>
            makeLiteralEntity(loading ? '...' : LL.FOLDER_EMPTY(), id);

        return getEntityTreeRows(null, getRelation, isLoading, getPlaceholder, canExpand, isExpanded, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        getEntity,
        getByRelation,
        isRelationLoading,
        canExpand,
        isExpanded,
        searchParentSet,
        tableVersion,
        LL,
        query.length,
    ]);

    return (
        <Sidebar className="tag-sidebar" $white>
            <SidebarSection>
                <BasicTitle style={{ fontSize: '14px', paddingBottom: 20 }}>{LL.DATABASE_AND_PROPERTIES()}</BasicTitle>
                <SearchInput value={query} name="search" onChange={setQuery} placeholder={LL.SEARCH() + '...'} />
            </SidebarSection>
            <SidebarScroller>
                <TagsTree items={treeRows} isExpanded={isExpanded} toggleExpand={expandClusters.toggleExpand} />
            </SidebarScroller>
        </Sidebar>
    );
};
